import { useState, useEffect } from "react";
import type { CSSProperties } from "react";
import type { HeroSlide } from "../types";

/**
 * Full-bleed background image rotator for the hero.
 * Crossfades between slides on a fixed interval (default 8s) and slowly
 * animates each active slide's scale (Ken Burns) from `scaleFrom` to
 * `scaleTo`, so each slide controls its own zoom-in / zoom-out. Each slide
 * can also set its own object-position to keep the subject framed.
 */
export function HeroCarousel({
	slides,
	interval = 8000,
}: {
	slides: HeroSlide[];
	interval?: number;
}) {
	const [current, setCurrent] = useState(0);

	useEffect(() => {
		if (slides.length <= 1) return;
		const t = setInterval(
			() => setCurrent((p) => (p + 1) % slides.length),
			interval
		);
		return () => clearInterval(t);
	}, [slides.length, interval]);

	return (
		<div className="hero-carousel">
			{slides.map((s, i) => {
				const from = s.scaleFrom ?? 1;
				const to = s.scaleTo ?? from;
				const style = {
					objectPosition: s.objectPosition,
					"--from": String(from),
					"--to": String(to),
					"--dur": `${interval}ms`,
				} as CSSProperties;
				return (
					<div
						className={`hero-carousel__slide${i === current ? " active" : ""}`}
						key={i}
						aria-hidden={i !== current}
					>
						<img src={s.src} alt="" style={style} />
					</div>
				);
			})}
			{slides.length > 1 && (
				<div className="hero-carousel__dots">
					{slides.map((_, i) => (
						<button
							key={i}
							className={`hero-carousel__dot${i === current ? " active" : ""}`}
							onClick={() => setCurrent(i)}
							aria-label={`Imagen ${i + 1}`}
						/>
					))}
				</div>
			)}
		</div>
	);
}
